import { useState } from 'react';
import type { ProgramPublicProfile, ProgramEpisode } from '@/mocks/programProfile';

interface Props {
  profile: ProgramPublicProfile;
}

export default function ProgramEpisodesSection({ profile }: Props) {
  const [activeTab, setActiveTab] = useState<'recientes' | 'especiales'>('recientes');
  const [savedEpisodes, setSavedEpisodes] = useState<Set<number>>(new Set());

  const toggleSave = (id: number) => {
    setSavedEpisodes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filtered = activeTab === 'especiales'
    ? profile.episodes.filter((e) => e.isSpecial)
    : profile.episodes;

  return (
    <section className="bg-background-50 rounded-2xl border border-background-200/70 p-5 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
            <i className="ri-headphone-line text-accent-600 text-sm"></i>
          </div>
          <h2 className="text-base font-bold text-foreground-950">Episodios</h2>
          <span className="text-xs text-foreground-400">{profile.episodes.length} episodios</span>
        </div>

        {/* Tabs */}
        <div className="flex items-center bg-background-100 rounded-full p-0.5 self-start">
          {(['recientes', 'especiales'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === tab
                  ? 'bg-background-50 text-foreground-950 shadow-sm'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              {tab === 'recientes' ? 'Recientes' : 'Especiales'}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-14 h-14 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-headphone-line text-2xl text-foreground-300"></i>
          </div>
          <p className="text-sm font-semibold text-foreground-500">Sin episodios {activeTab === 'especiales' ? 'especiales' : 'recientes'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((ep) => (
            <EpisodeCard key={ep.id} episode={ep} isSaved={savedEpisodes.has(ep.id)} onToggleSave={() => toggleSave(ep.id)} />
          ))}
        </div>
      )}
    </section>
  );
}

function EpisodeCard({ episode, isSaved, onToggleSave }: { episode: ProgramEpisode; isSaved: boolean; onToggleSave: () => void }) {
  return (
    <div className="flex flex-col sm:flex-row gap-4 bg-background-100/70 rounded-xl border border-background-200/70 p-4 hover:border-accent-300/60 transition-colors cursor-pointer group">
      {/* Thumbnail */}
      <div className="w-full sm:w-48 h-28 rounded-lg overflow-hidden shrink-0 relative">
        <img src={episode.thumbnailUrl} alt={episode.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center">
            <i className="ri-play-fill text-accent-600 text-lg ml-0.5"></i>
          </div>
        </div>
        {episode.isSpecial && (
          <span className="absolute top-2 left-2 px-1.5 py-0.5 rounded-md bg-accent-500 text-white text-[9px] font-bold">
            ESPECIAL
          </span>
        )}
        <span className="absolute bottom-2 right-2 px-1.5 py-0.5 rounded-md bg-black/60 text-white text-[9px] font-medium">
          {episode.duration}
        </span>
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-foreground-900 mb-1 line-clamp-1">{episode.title}</h3>
            <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-2">{episode.description}</p>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onToggleSave(); }}
            className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-colors cursor-pointer ${
              isSaved ? 'bg-accent-100 text-accent-600' : 'bg-background-200 text-foreground-400 hover:text-foreground-600'
            }`}
          >
            <i className={`${isSaved ? 'ri-bookmark-fill' : 'ri-bookmark-line'} text-xs`}></i>
          </button>
        </div>

        <div className="flex items-center gap-3 text-[10px] text-foreground-400 flex-wrap">
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line"></i>
            {new Date(episode.date).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-headphone-line"></i>
            {formatEpisodeListens(episode.listens)} reproducciones
          </span>
          {episode.guestName && (
            <span className="flex items-center gap-1 text-accent-600 font-medium">
              <i className="ri-user-star-line"></i>
              {episode.guestName}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function formatEpisodeListens(n: number): string {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(0) + 'K';
  return n.toString();
}